import api, { getCurrentMode } from "../utils/api";
import { getClosestPredefinedColor } from "../utils/colorConstants";

// Color name to hex mapping for common colors
const colorNameToHex = {
  aliceblue: "#f0f8ff",
  antiquewhite: "#faebd7",
  aqua: "#00ffff",
  aquamarine: "#7fffd4",
  azure: "#f0ffff",
  beige: "#f5f5dc",
  bisque: "#ffe4c4",
  black: "#000000",
  blanchedalmond: "#ffebcd",
  blue: "#0000ff",
  blueviolet: "#8a2be2",
  brown: "#a52a2a",
  burlywood: "#deb887",
  cadetblue: "#5f9ea0",
  chartreuse: "#7fff00",
  chocolate: "#d2691e",
  coral: "#ff7f50",
  cornflowerblue: "#6495ed",
  cornsilk: "#fff8dc",
  crimson: "#dc143c",
  cyan: "#00ffff",
  darkblue: "#00008b",
  darkcyan: "#008b8b",
  darkgoldenrod: "#b8860b",
  darkgray: "#a9a9a9",
  darkgreen: "#006400",
  darkgrey: "#a9a9a9",
  darkkhaki: "#bdb76b",
  darkmagenta: "#8b008b",
  darkolivegreen: "#556b2f",
  darkorange: "#ff8c00",
  darkorchid: "#9932cc",
  darkred: "#8b0000",
  darksalmon: "#e9967a",
  darkseagreen: "#8fbc8f",
  darkslateblue: "#483d8b",
  darkslategray: "#2f4f4f",
  darkslategrey: "#2f4f4f",
  darkturquoise: "#00ced1",
  darkviolet: "#9400d3",
  deeppink: "#ff1493",
  deepskyblue: "#00bfff",
  dimgray: "#696969",
  dimgrey: "#696969",
  dodgerblue: "#1e90ff",
  firebrick: "#b22222",
  floralwhite: "#fffaf0",
  forestgreen: "#228b22",
  fuchsia: "#ff00ff",
  gainsboro: "#dcdcdc",
  ghostwhite: "#f8f8ff",
  gold: "#ffd700",
  goldenrod: "#daa520",
  gray: "#808080",
  green: "#008000",
  greenyellow: "#adff2f",
  grey: "#808080",
  honeydew: "#f0fff0",
  hotpink: "#ff69b4",
  indianred: "#cd5c5c",
  indigo: "#4b0082",
  ivory: "#fffff0",
  khaki: "#f0e68c",
  lavender: "#e6e6fa",
  lavenderblush: "#fff0f5",
  lawngreen: "#7cfc00",
  lemonchiffon: "#fffacd",
  lightblue: "#add8e6",
  lightcoral: "#f08080",
  lightcyan: "#e0ffff",
  lightgoldenrodyellow: "#fafad2",
  lightgray: "#d3d3d3",
  lightgreen: "#90ee90",
  lightgrey: "#d3d3d3",
  lightpink: "#ffb6c1",
  lightsalmon: "#ffa07a",
  lightseagreen: "#20b2aa",
  lightskyblue: "#87cefa",
  lightslategray: "#778899",
  lightslategrey: "#778899",
  lightsteelblue: "#b0c4de",
  lightyellow: "#ffffe0",
  lime: "#00ff00",
  limegreen: "#32cd32",
  linen: "#faf0e6",
  magenta: "#ff00ff",
  maroon: "#800000",
  mediumaquamarine: "#66cdaa",
  mediumblue: "#0000cd",
  mediumorchid: "#ba55d3",
  mediumpurple: "#9370db",
  mediumseagreen: "#3cb371",
  mediumslateblue: "#7b68ee",
  mediumspringgreen: "#00fa9a",
  mediumturquoise: "#48d1cc",
  mediumvioletred: "#c71585",
  midnightblue: "#191970",
  mintcream: "#f5fffa",
  mistyrose: "#ffe4e1",
  moccasin: "#ffe4b5",
  navajowhite: "#ffdead",
  navy: "#000080",
  oldlace: "#fdf5e6",
  olive: "#808000",
  olivedrab: "#6b8e23",
  orange: "#ffa500",
  orangered: "#ff4500",
  orchid: "#da70d6",
  palegoldenrod: "#eee8aa",
  palegreen: "#98fb98",
  paleturquoise: "#afeeee",
  palevioletred: "#db7093",
  papayawhip: "#ffefd5",
  peachpuff: "#ffdab9",
  peru: "#cd853f",
  pink: "#ffc0cb",
  plum: "#dda0dd",
  powderblue: "#b0e0e6",
  purple: "#800080",
  rebeccapurple: "#663399",
  red: "#ff0000",
  rosybrown: "#bc8f8f",
  royalblue: "#4169e1",
  saddlebrown: "#8b4513",
  salmon: "#fa8072",
  sandybrown: "#f4a460",
  seagreen: "#2e8b57",
  seashell: "#fff5ee",
  sienna: "#a0522d",
  silver: "#c0c0c0",
  skyblue: "#87ceeb",
  slateblue: "#6a5acd",
  slategray: "#708090",
  slategrey: "#708090",
  snow: "#fffafa",
  springgreen: "#00ff7f",
  steelblue: "#4682b4",
  tan: "#d2b48c",
  teal: "#008080",
  thistle: "#d8bfd8",
  tomato: "#ff6347",
  turquoise: "#40e0d0",
  violet: "#ee82ee",
  wheat: "#f5deb3",
  white: "#ffffff",
  whitesmoke: "#f5f5f5",
  yellow: "#ffff00",
  yellowgreen: "#9acd32",
};

// Function to convert color name to hex and ensure it's one of our predefined colors
const validateColor = (color) => {
  if (!color) return "#2196f3"; // Default blue color

  // If it's already a valid hex, rgb, rgba, hsl, or hsla color
  if (/^(#|rgb|rgba|hsl|hsla)/.test(color)) {
    // Map it to one of our predefined colors
    return getClosestPredefinedColor(color);
  }

  // If it's a color name, convert to hex
  const colorLower = color.toLowerCase();
  if (colorNameToHex[colorLower]) {
    // Map the hex to one of our predefined colors
    return getClosestPredefinedColor(colorNameToHex[colorLower]);
  }

  // If all else fails, return the default blue color
  return "#2196f3";
};

// Function to transform frontend data back to backend format
const transformToBackendFormat = (columns) => {
  return columns.map((column) => ({
    ...column,
    _id: column.id, // Convert id back to _id for MongoDB
    recruits: column.recruits.map((recruit) => {
      // CRITICAL: NEVER overwrite an existing prospect object
      // If recruit.prospect exists and has a proper structure, keep it exactly as is
      let prospectField;

      if (
        recruit.prospect &&
        typeof recruit.prospect === "object" &&
        recruit.prospect._id
      ) {
        // Keep the existing prospect object exactly as is
        prospectField = recruit.prospect;
      } else if (recruit.prospect && typeof recruit.prospect === "string") {
        // If prospect is just a string ID, convert it to an object
        prospectField = { _id: recruit.prospect };
      } else {
        // Only create a minimal prospect object if one doesn't exist at all
        prospectField = { _id: recruit.id || recruit._id };
      }

      return {
        ...recruit,
        prospect: prospectField,
        // Remove the transformed id field to avoid conflicts
        id: undefined,
        // Remove notes field - notes are now only stored in the prospect entity
        notes: undefined,
      };
    }),
  }));
};

// Create a shared storage for boards that persists between components
// This simulates a database or API in sandbox mode
class BoardsStorage {
  constructor() {
    // Initialize with default boards if storage is empty
    if (!this.getBoards().length) {
      this.boards = [
        {
          id: "board-1",
          name: "2026 Recruits",
          description: "Top prospects for 2026 class",
          order: 0,
          columns: [
            {
              id: "column-1",
              title: "Prospects",
              color: "#9e9e9e",
              recruits: [
                {
                  id: "recruit-1",
                  firstName: "John",
                  lastName: "Doe",
                  position: "QB",
                  classYear: "2026",
                  highSchool: "Lincoln High",
                  city: "San Francisco",
                  state: "CA",
                  image: null,
                  color: null,
                },
                {
                  id: "recruit-2",
                  firstName: "Mike",
                  lastName: "Smith",
                  position: "RB",
                  classYear: "2026",
                  highSchool: "Washington High",
                  city: "Dallas",
                  state: "TX",
                  image: null,
                  color: "green",
                },
              ],
            },
            {
              id: "column-2",
              title: "Contacted",
              color: "#2196f3",
              recruits: [
                {
                  id: "recruit-3",
                  firstName: "Sarah",
                  lastName: "Johnson",
                  position: "PG",
                  classYear: "2026",
                  highSchool: "Jefferson High",
                  city: "Miami",
                  state: "FL",
                  image: null,
                  color: "blue",
                },
              ],
            },
            {
              id: "column-3",
              title: "Interested",
              color: "#ff9800",
              recruits: [],
            },
            {
              id: "column-4",
              title: "Offered",
              color: "#f44336",
              recruits: [],
            },
            {
              id: "column-5",
              title: "Committed",
              color: "#4caf50",
              recruits: [],
            },
          ],
        },
        {
          id: "board-2",
          name: "2027 Recruits",
          description: "Early prospects for 2027 class",
          order: 1,
          columns: [
            {
              id: "column-6",
              title: "Prospects",
              color: "#9e9e9e",
              recruits: [],
            },
            {
              id: "column-7",
              title: "Contacted",
              color: "#2196f3",
              recruits: [],
            },
            {
              id: "column-8",
              title: "Interested",
              color: "#ff9800",
              recruits: [],
            },
          ],
        },
      ];

      // Save initial boards to localStorage
      this.saveBoards();
    }
  }

  getBoards() {
    try {
      const storedBoards = localStorage.getItem("teamscout_boards");
      const boards = storedBoards ? JSON.parse(storedBoards) : [];
      // Sort boards by order field
      return boards.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
      console.error("Error retrieving boards from localStorage:", error);
      return [];
    }
  }

  saveBoards() {
    try {
      localStorage.setItem("teamscout_boards", JSON.stringify(this.boards));
    } catch (error) {
      console.error("Error saving boards to localStorage:", error);
    }
  }

  addBoard(board) {
    this.boards = [...this.getBoards(), board];
    this.saveBoards();
    return board;
  }

  updateBoard(boardId, updatedBoard) {
    const boards = this.getBoards();
    this.boards = boards.map((board) =>
      board.id === boardId ? { ...board, ...updatedBoard } : board
    );
    this.saveBoards();
    return this.boards.find((board) => board.id === boardId);
  }

  deleteBoard(boardId) {
    const boards = this.getBoards();
    this.boards = boards.filter((board) => board.id !== boardId);
    this.saveBoards();
    return true;
  }

  addProspectToBoard(boardId, columnId, prospect) {
    const boards = this.getBoards();
    const boardIndex = boards.findIndex((b) => b.id === boardId);

    if (boardIndex >= 0) {
      const board = boards[boardIndex];
      const columnIndex = board.columns.findIndex((c) => c.id === columnId);

      if (columnIndex >= 0) {
        const column = board.columns[columnIndex];
        const existingProspectIndex = column.recruits.findIndex(
          (r) => r.id === prospect.id
        );

        if (existingProspectIndex === -1) {
          // If prospect has no color or invalid color, inherit column color
          const prospectColor =
            prospect.color && prospect.color.trim() !== ""
              ? validateColor(prospect.color)
              : column.color;

          // Add prospect to column
          column.recruits.push({
            ...prospect,
            color: prospectColor, // Use column color as default
            addedAt: new Date().toISOString(), // Add timestamp for when prospect was added
          });

          // Update the board in the array
          boards[boardIndex] = board;
          this.boards = boards;
          this.saveBoards();
        }
      }
    }

    return { success: true };
  }

  updateBoardOrder(boardOrders) {
    const boards = this.getBoards();

    // Update order for each board
    boardOrders.forEach(({ boardId, order }) => {
      const boardIndex = boards.findIndex((b) => b.id === boardId);
      if (boardIndex >= 0) {
        boards[boardIndex].order = order;
      }
    });

    this.boards = boards;
    this.saveBoards();
    return true;
  }
}

// Create a singleton instance
const boardsStorage = new BoardsStorage();

/**
 * Get all boards
 * @returns {Promise<Array>} Array of boards
 */
export const getBoards = async () => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Get boards from storage
    const boards = boardsStorage.getBoards();

    // Validate all column colors before returning
    const validatedBoards = boards.map((board) => ({
      ...board,
      columns: board.columns.map((column) => ({
        ...column,
        color: validateColor(column.color),
      })),
    }));

    return validatedBoards;
  }

  // Real API call
  try {
    const response = await api.boards.getAll();
    // Transform MongoDB _id to id for frontend compatibility
    const transformedBoards = response.map((board) => ({
      ...board,
      id: board._id,
      columns: board.columns.map((column) => ({
        ...column,
        id: column._id,
        recruits: column.recruits.map((recruit) => ({
          ...recruit,
          // Handle both cases: prospect as object or as string ID
          id:
            typeof recruit.prospect === "string"
              ? recruit.prospect
              : recruit.prospect._id,
        })),
      })),
    }));

    // Sort boards by order field
    transformedBoards.sort((a, b) => (a.order || 0) - (b.order || 0));
    return transformedBoards;
  } catch (error) {
    console.error("Error fetching boards:", error);
    throw error;
  }
};

/**
 * Create a new board
 * @param {Object} boardData - Board data
 * @returns {Promise<Object>} Created board
 */
export const createBoard = async (boardData) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Create a new board with columns based on template
    const template = (boardData.template || "offerStatus").toString();
    let columns = [];

    const buildCol = (suffix, title, color) => ({
      id: `column-${Date.now()}-${suffix}`,
      title,
      color: validateColor(color),
      recruits: [],
    });

    if (template === "blank") {
      columns = [];
    } else if (
      template === "fivePositions" ||
      template === "positions5" ||
      template === "positions"
    ) {
      // Order: RB, LB, QB, TE, WR
      columns = [
        buildCol(1, "RB", "#4caf50"),
        buildCol(2, "LB", "#9c27b0"),
        buildCol(3, "QB", "#2196f3"),
        buildCol(4, "TE", "#ff9800"),
        buildCol(5, "WR", "#f44336"),
      ];
    } else {
      // Default offer status pipeline
      columns = [
        buildCol(1, "Prospects", "#9e9e9e"),
        buildCol(2, "Contacted", "#2196f3"),
        buildCol(3, "Interested", "#ff9800"),
        buildCol(4, "Offered", "#f44336"),
        buildCol(5, "Committed", "#4caf50"),
      ];
    }

    // Get the highest order number and increment it for the new board
    const existingBoards = boardsStorage.getBoards();
    const maxOrder =
      existingBoards.length > 0
        ? Math.max(...existingBoards.map((b) => b.order || 0))
        : -1;

    const newBoard = {
      id: `board-${Date.now()}`,
      name: boardData.name,
      description: boardData.description || "",
      template,
      order: maxOrder + 1,
      columns,
    };

    // Add the new board to storage
    boardsStorage.addBoard(newBoard);

    // In a real app, we would update the server
    // For now, just return the new board
    return newBoard;
  }

  // Real API call
  try {
    const response = await api.boards.create(boardData);
    // Transform MongoDB _id to id for frontend compatibility
    const transformedBoard = {
      ...response,
      id: response._id,
      columns: response.columns.map((column) => ({
        ...column,
        id: column._id,
        recruits: column.recruits.map((recruit) => ({
          ...recruit,
          id: recruit._id,
        })),
      })),
    };
    return transformedBoard;
  } catch (error) {
    console.error("Error creating board:", error);
    throw error;
  }
};

/**
 * Add a prospect to a board column
 * @param {string} boardId - Board ID
 * @param {string} columnId - Column ID
 * @param {Object} prospect - Prospect data
 * @returns {Promise<Object>} Updated board
 */
export const addProspectToBoard = async (boardId, columnId, prospect) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Add prospect to board in storage
    boardsStorage.addProspectToBoard(boardId, columnId, prospect);

    // In a real app, we would update the server
    // For now, just return success
    return {
      success: true,
      message: `Added ${prospect.firstName} ${prospect.lastName} to board`,
    };
  }

  // Real API call
  try {
    // Get the column color to use as default if prospect has no color
    const boards = await getBoards();
    const board = boards.find((b) => b.id === boardId);
    const column = board?.columns?.find((c) => c.id === columnId);

    // Use column color as default if prospect has no color
    const prospectColor =
      prospect.color && prospect.color.trim() !== ""
        ? prospect.color
        : column?.color || "#2196f3";

    const response = await api.post(
      `/boards/${boardId}/columns/${columnId}/recruits`,
      {
        prospectId: prospect.id,
        color: prospectColor,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding prospect to board:", error);
    throw error;
  }
};

/**
 * Delete a board
 * @param {string} boardId - Board ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteBoard = async (boardId) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Delete the board from storage
    boardsStorage.deleteBoard(boardId);

    // In a real app, we would delete from the server
    // For now, just return success
    return true;
  }

  // Real API call
  try {
    await api.boards.delete(boardId);
    return true;
  } catch (error) {
    console.error("Error deleting board:", error);
    throw error;
  }
};

/**
 * Update a board
 * @param {string} boardId - Board ID
 * @param {Object} boardData - Updated board data
 * @returns {Promise<Object>} Updated board
 */
export const updateBoard = async (boardId, boardData) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Update the board in storage
    const updatedBoard = boardsStorage.updateBoard(boardId, boardData);

    // In a real app, we would update the server
    // For now, just return the updated board
    return updatedBoard;
  }

  // Real API call
  try {
    // Transform the data back to backend format if columns are being updated
    let dataToSend = boardData;
    if (boardData.columns) {
      dataToSend = {
        ...boardData,
        columns: transformToBackendFormat(boardData.columns),
      };
    }

    const response = await api.put(`/boards/${boardId}`, dataToSend);
    // Transform MongoDB _id to id for frontend compatibility
    const transformedBoard = {
      ...response.data,
      id: response.data._id,
      columns: response.data.columns.map((column) => ({
        ...column,
        id: column._id,
        recruits: column.recruits.map((recruit) => ({
          ...recruit,
          // Handle both cases: prospect as object or as string ID
          id:
            typeof recruit.prospect === "string"
              ? recruit.prospect
              : recruit.prospect._id,
        })),
      })),
    };
    return transformedBoard;
  } catch (error) {
    console.error("Error updating board:", error);
    throw error;
  }
};

/**
 * Update a recruit in a board column
 * @param {string} boardId - Board ID
 * @param {string} columnId - Column ID
 * @param {string} recruitId - Recruit ID
 * @param {Object} updates - Updates to apply (color, notes)
 * @returns {Promise<Object>} Updated board
 */
export const updateRecruit = async (boardId, columnId, recruitId, updates) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Update the recruit in storage
    const boards = boardsStorage.getBoards();
    const boardIndex = boards.findIndex((b) => b.id === boardId);

    if (boardIndex >= 0) {
      const board = boards[boardIndex];
      const columnIndex = board.columns.findIndex((c) => c.id === columnId);

      if (columnIndex >= 0) {
        const column = board.columns[columnIndex];
        const recruitIndex = column.recruits.findIndex(
          (r) => r.id === recruitId
        );

        if (recruitIndex >= 0) {
          // Update only the specific fields, preserve all others
          column.recruits[recruitIndex] = {
            ...column.recruits[recruitIndex],
            ...updates,
          };

          // Update the board in storage
          boardsStorage.updateBoard(boardId, board);
        }
      }
    }

    // In a real app, we would update the server
    // For now, just return success
    return { success: true };
  }

  // Real API call - only send the fields that are being updated
  try {
    const response = await api.put(
      `/boards/${boardId}/columns/${columnId}/recruits/${recruitId}`,
      updates
    );
    return response.data;
  } catch (error) {
    console.error("Error updating recruit:", error);
    throw error;
  }
};

/**
 * Update board order
 * @param {Array} boardOrders - Array of objects with boardId and order
 * @returns {Promise<boolean>} Success status
 */
export const updateBoardOrder = async (boardOrders) => {
  // Check if we're in sandbox mode
  const mode = getCurrentMode();

  if (mode === "sandbox") {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Update board order in storage
    return boardsStorage.updateBoardOrder(boardOrders);
  }

  // Real API call
  try {
    await api.put("/boards/order", { boardOrders });
    return true;
  } catch (error) {
    console.error("Error updating board order:", error);
    throw error;
  }
};

const boardService = {
  getBoards,
  createBoard,
  addProspectToBoard,
  deleteBoard,
  updateBoard,
  updateRecruit,
  updateBoardOrder,
};

export default boardService;
